// =============================================================================
// src/views/comparador/comparador-donut.ts — Donut markers do modo "Sobreposicao".
//
// Proveniencia: assets/unified-app.js Sections.comparador._buildDonutSVG /
//   _applyOverlayMode / _removeDonutMarkers (linhas 1215-1272). Portado FIEL:
//   raio 20, cx/cy 26, strokeW 10, iconSize [52,52], iconAnchor [26,26], rotacao
//   -90deg, dash-array/offset por segmento e o circulo branco interno (opacity
//   0.92). Cores vem de MANAUS_GROUPS (A/B/C). NAO alterar geometria/opacidades —
//   sao paridade visual.
//
// Leaflet: L.divIcon({ html, iconSize, iconAnchor, className }) + L.marker(latlng,
//   { icon }).addTo(map) + map.removeLayer(marker). API verificada via Context7
//   (/leaflet/leaflet) em 2026-05-30 — mesma superficie usada por core/map-controller.
//
// SRP: so o SVG do donut e o ciclo de vida dos marcadores. A escolha de zona/modo
//   e os estilos de zona vivem em comparador-modes.ts.
// =============================================================================

import L from 'leaflet';
import type { Map as LeafletMap, Marker, LatLngExpression } from 'leaflet';
import type { GroupKey } from '../../app/types';
import { MANAUS_GROUPS } from '../../data';

/** Raio do anel do donut em px (_buildDonutSVG r=20). */
const DONUT_RADIUS = 20;
/** Centro X/Y do SVG 52x52 (_buildDonutSVG cx=cy=26). */
const DONUT_CENTER = 26;
/** Espessura do anel em px (_buildDonutSVG strokeW=10). */
const DONUT_STROKE_WIDTH = 10;
/** Lado do icone em px (iconSize [52,52]). */
const DONUT_ICON_SIZE = 52;

/** Circunferencia do anel (2*pi*r), base do dash-array de cada segmento. */
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Zera o percentual de um grupo inativo (replica os 3 ifs de _buildDonutSVG). */
function maskInactiveGroup(pct: number, key: GroupKey, activeKeys: readonly GroupKey[]): number {
  return activeKeys.indexOf(key) < 0 ? 0 : pct;
}

/** Markup de um anel (circle) com dash-array/offset do segmento (FIEL ao original). */
function renderDonutRing(color: string, segment: number, offset: number): string {
  return (
    '<circle cx="' + DONUT_CENTER + '" cy="' + DONUT_CENTER + '" r="' + DONUT_RADIUS + '"' +
    ' fill="none" stroke="' + color + '" stroke-width="' + DONUT_STROKE_WIDTH + '"' +
    ' stroke-dasharray="' + segment + ' ' + DONUT_CIRCUMFERENCE + '"' +
    ' stroke-dashoffset="-' + offset + '"/>'
  );
}

/**
 * Constroi o SVG do donut a partir das fracoes A/B/C (replica _buildDonutSVG).
 * Os segmentos sao encadeados (offsetB = circ - segA, offsetC = circ - segA - segB),
 * preservando a ordem/empilhamento exatos do original. Rotacao -90deg no <svg>.
 */
export function buildComparadorDonutSvg(
  pctA: number,
  pctB: number,
  pctC: number,
  activeKeys: readonly GroupKey[],
): string {
  const fracA = maskInactiveGroup(pctA, 'A', activeKeys);
  const fracB = maskInactiveGroup(pctB, 'B', activeKeys);
  const fracC = maskInactiveGroup(pctC, 'C', activeKeys);
  const segA = DONUT_CIRCUMFERENCE * fracA;
  const segB = DONUT_CIRCUMFERENCE * fracB;
  const segC = DONUT_CIRCUMFERENCE * fracC;
  const innerR = DONUT_RADIUS - DONUT_STROKE_WIDTH / 2 - 1;
  return (
    '<svg width="' + DONUT_ICON_SIZE + '" height="' + DONUT_ICON_SIZE + '" style="transform:rotate(-90deg)">' +
    renderDonutRing(MANAUS_GROUPS.A.color, segA, 0) +
    renderDonutRing(MANAUS_GROUPS.B.color, segB, DONUT_CIRCUMFERENCE - segA) +
    renderDonutRing(MANAUS_GROUPS.C.color, segC, DONUT_CIRCUMFERENCE - segA - segB) +
    '<circle cx="' + DONUT_CENTER + '" cy="' + DONUT_CENTER + '" r="' + innerR + '" fill="white" opacity="0.92"/>' +
    '</svg>'
  );
}

/** Cria o marcador-donut numa zona e o adiciona ao mapa (replica o corpo do forEach). */
export function addComparadorDonutMarker(
  map: LeafletMap,
  centroid: LatLngExpression,
  donutSvg: string,
): Marker {
  const icon = L.divIcon({
    className: '',
    html: donutSvg,
    iconSize: [DONUT_ICON_SIZE, DONUT_ICON_SIZE],
    iconAnchor: [DONUT_CENTER, DONUT_CENTER],
  });
  const marker = L.marker(centroid, { icon });
  marker.addTo(map);
  return marker;
}
